// ========================================
// Program View - Core Initialization
// ========================================

const ProgramView = {
    currentMonth: new Date(),
    currentFormation: null,
    currentCategory: null,
    selectedSubjectId: null, // Track selected subject for assignment
    selectedInstanceId: null, // Track specific instance (clone) being selected
    isSelectingNewSubject: false, // UI state for "Add Subject" mode
    selectedClassroom: 'Aula 1', // Selected classroom for quick schedule assigning
    subjectColors: {},
    visibleSubjects: [], // Initialize empty array

    init() {
        // Load formations and set initial state
        let formations = DataManager.getFormations();
        if (AuthManager.isStudent()) {
            const studentId = AuthManager.getStudentId();
            if (studentId) {
                const student = DataManager.getById(DataManager.KEYS.STUDENTS, studentId);
                if (student && student.formations) {
                    formations = formations.filter(f => student.formations.includes(f.id));
                } else {
                    formations = [];
                }
            }
        }

        if (AuthManager.isProfessor() && !AuthManager.hasModule('plan-estudios')) {
            const professorId = AuthManager.getProfessorId();
            if (professorId) {
                formations = formations.filter(f => {
                    const hasSubject = DataManager.getSubjectsByFormation(f.id).some(s => s.professorId === professorId);
                    const hasClass = DataManager.getClassesByFormation(f.id).some(c => c.professorId === professorId);
                    return hasSubject || hasClass;
                });
            } else {
                formations = [];
            }
        }

        if (formations.length > 0) {
            if (!this.currentCategory) {
                this.currentCategory = formations[0].type;
            }
            if (!this.currentFormation) {
                this.currentFormation = formations.find(f => f.type === this.currentCategory) || formations[0];
            }
        }

        // Correct category if formation doesn't match
        if (this.currentFormation && this.currentFormation.type !== this.currentCategory) {
            this.currentCategory = this.currentFormation.type;
        }

        // Initialize visible subjects based on existing assignments
        this.initializeVisibleSubjects();

        // Pre-assign colors to all subjects in this formation to ensure consistency
        if (this.currentFormation) {
            const subjects = DataManager.getSubjectsByFormation(this.currentFormation.id);
            subjects.forEach((s, i) => this.getColorForSubject(s.id, i));
        }

        this.renderCalendar();
        this.renderSubjects();
        this.renderExtraSessions();
        this.renderDashboard();
        this.updateSummary();
        // this.attachEventListeners(); // Handled by App.js to avoid double binding
    },

    loadCurrentFormation() {
        let formations = DataManager.getFormations();
        if (AuthManager.isStudent()) {
            const studentId = AuthManager.getStudentId();
            if (studentId) {
                const student = DataManager.getById(DataManager.KEYS.STUDENTS, studentId);
                if (student && student.formations) {
                    formations = formations.filter(f => student.formations.includes(f.id));
                } else {
                    formations = [];
                }
            }
        }

        if (AuthManager.isProfessor() && !AuthManager.hasModule('plan-estudios')) {
            const professorId = AuthManager.getProfessorId();
            if (professorId) {
                formations = formations.filter(f => {
                    const hasSubject = DataManager.getSubjectsByFormation(f.id).some(s => s.professorId === professorId);
                    const hasClass = DataManager.getClassesByFormation(f.id).some(c => c.professorId === professorId);
                    return hasSubject || hasClass;
                });
            } else {
                formations = [];
            }
        }

        // If no formation selected, or current one deleted, pick one
        // Use loose comparison or String conversion for ID check to handle type mismatches
        if (!this.currentFormation || !formations.find(f => String(f.id) === String(this.currentFormation.id))) {
            if (formations.length > 0) {
                this.currentFormation = formations[0];
                this.currentCategory = this.currentFormation.type;
            } else {
                this.currentFormation = null;
                this.currentCategory = null;
            }
        } else {
            // Only sync category if it's not set
            if (!this.currentCategory) {
                this.currentCategory = this.currentFormation.type;
            }
        }
    },

    attachEventListeners() {
        // Calendar navigation
        document.getElementById('prev-month')?.addEventListener('click', () => {
            this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
            this.initializeVisibleSubjects(); // Re-sync visible subjects with raw data
            this.renderCalendar();
        });

        document.getElementById('next-month')?.addEventListener('click', () => {
            // Robust navigation: Always go to the 1st of the target month
            this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
            this.initializeVisibleSubjects(); // Re-sync visible subjects with raw data
            this.renderCalendar();
        });

        // Tab Handling (Drag & Drop + Click) - Use delegation
        const tabsContainer = document.getElementById('formation-tabs');
        if (tabsContainer) {
            // Click handling via delegation
            tabsContainer.addEventListener('click', (e) => {
                const tab = e.target.closest('.formation-tab-item');
                if (tab && !e.target.closest('.drag-handle')) { // Allow drag handle to be separate if needed
                    const formationId = tab.dataset.id;
                    if (formationId && (!this.currentFormation || this.currentFormation.id !== formationId)) {
                        this.switchFormation(formationId);
                    }
                }
            });

            // Drag & Drop
            tabsContainer.addEventListener('dragstart', (e) => {
                const tab = e.target.closest('.formation-tab-item');
                if (tab) {
                    e.dataTransfer.setData('text/plain', tab.dataset.id);
                    tab.classList.add('dragging');
                    e.dataTransfer.effectAllowed = 'move';
                }
            });

            tabsContainer.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const tab = e.target.closest('.formation-tab-item');
                if (tab) tab.classList.add('drag-over');
            });

            tabsContainer.addEventListener('dragleave', (e) => {
                const tab = e.target.closest('.formation-tab-item');
                if (tab) tab.classList.remove('drag-over');
            });

            tabsContainer.addEventListener('drop', (e) => {
                e.preventDefault();
                const tab = e.target.closest('.formation-tab-item');
                if (tab) {
                    tab.classList.remove('drag-over');
                    const draggedId = e.dataTransfer.getData('text/plain');
                    const targetId = tab.dataset.id;

                    document.querySelectorAll('.formation-tab-item').forEach(t => t.classList.remove('dragging'));

                    if (draggedId && targetId && draggedId !== targetId) {
                        this.reorderFormations(draggedId, targetId);
                    }
                }
            });
        }

        // New subject button
        const btn = document.getElementById('new-subject-btn');
        if (btn) {
            // Use cloneNode to wipe listeners
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);

            newBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                console.log('Button clicked! Current state:', ProgramView.isSelectingNewSubject);

                // Ensure formation is loaded
                if (!ProgramView.currentFormation) {
                    ProgramView.loadCurrentFormation();
                }

                if (ProgramView.isSelectingNewSubject) {
                    console.log('Already selecting');
                    return;
                }

                ProgramView.isSelectingNewSubject = true;
                ProgramView.renderSubjects();

                setTimeout(() => {
                    const list = document.getElementById('subjects-list');
                    if (list) list.scrollTop = list.scrollHeight;
                }, 50);
            });
        }

        // Export PDF
        const exportBtn = document.getElementById('program-export-pdf');
        if (exportBtn) {
            exportBtn.addEventListener('click', () => {
                this.openExportModal();
            });
        }

        // Subject List DnD Handlers
        const subjectsList = document.getElementById('subjects-list');
        if (subjectsList) {

            subjectsList.addEventListener('dragstart', (e) => {
                const item = e.target.closest('.subject-item');
                if (item) {
                    const instanceId = item.dataset.instanceId;
                    e.dataTransfer.setData('text/plain', instanceId);
                    e.dataTransfer.effectAllowed = 'move';
                    item.classList.add('dragging');

                    // Interaction feedback
                    setTimeout(() => item.style.opacity = '0.5', 0);
                }
            });

            subjectsList.addEventListener('dragend', (e) => {
                const item = e.target.closest('.subject-item');
                if (item) {
                    item.classList.remove('dragging');
                    item.style.opacity = '1';
                }
            });

            subjectsList.addEventListener('dragover', (e) => {
                e.preventDefault(); // Necessary to allow dropping
                e.dataTransfer.dropEffect = 'move';
                const item = e.target.closest('.subject-item');
                if (item) item.classList.add('drag-over');
            });

            subjectsList.addEventListener('dragleave', (e) => {
                const item = e.target.closest('.subject-item');
                if (item) item.classList.remove('drag-over');
            });

            subjectsList.addEventListener('drop', (e) => {
                e.preventDefault();
                const item = e.target.closest('.subject-item');
                if (item) {
                    item.classList.remove('drag-over');
                    document.querySelectorAll('.subject-item.dragging').forEach(el => {
                        el.classList.remove('dragging');
                        el.style.opacity = '1';
                    });

                    const draggedInstanceId = e.dataTransfer.getData('text/plain');
                    const targetInstanceId = item.dataset.instanceId;

                    if (draggedInstanceId && targetInstanceId && draggedInstanceId !== targetInstanceId) {
                        this.reorderSubjects(draggedInstanceId, targetInstanceId);
                    }
                }
            });
        } else {
            console.error('Subject list container not found during event attachment');
        }

        // ── Global deselect: click outside subjects panel clears selection ──
        // Remove any previously attached version first to prevent stacking
        if (this._globalDeselectHandler) {
            document.removeEventListener('click', this._globalDeselectHandler, true);
        }

        this._globalDeselectHandler = (e) => {
            // Only act when a subject is currently selected
            if (!this.selectedSubjectId && !this.isSelectingNewSubject) return;

            // Allow clicks INSIDE the subjects list or the calendar grid (for assigning days)
            const subjectPanel = document.getElementById('subjects-list');
            const calendarGrid = document.getElementById('calendar-grid');
            const extraSessions = document.querySelector('.extra-sessions-section');

            const insidePanel = subjectPanel && subjectPanel.contains(e.target);
            const insideCalendar = calendarGrid && calendarGrid.contains(e.target);
            const insideExtra = extraSessions && extraSessions.contains(e.target);

            // Also allow clicks on any modal/popup (Swal) overlay
            const insideModal = e.target.closest('.swal2-container') || e.target.closest('#modal-overlay');

            if (!insidePanel && !insideCalendar && !insideExtra && !insideModal) {
                if (this.selectedSubjectId || this.isSelectingNewSubject) {
                    this.selectedSubjectId = null;
                    this.selectedInstanceId = null;
                    this.isSelectingNewSubject = false;
                    this.renderSubjects();
                    this.renderCalendar();
                }
            }
        };

        // Use capture phase so it fires before any inner stopPropagation
        document.addEventListener('click', this._globalDeselectHandler, true);
    },

    openClassroomsMap() {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const monthStr = String(month + 1).padStart(2, '0');
        const dayStr = String(day).padStart(2, '0');
        const dateString = `${year}-${monthStr}-${dayStr}`;

        // Get classes running today
        const allClasses = DataManager.getAll(DataManager.KEYS.CLASSES) || [];
        const todayClasses = allClasses.filter(c => c.startDate === dateString);

        const getClassroomStatus = (name) => {
            const current = todayClasses.find(c => c.classroom === name);
            if (current) {
                const subject = DataManager.getSubject(current.subjectId);
                const professor = DataManager.getProfessor(current.professorId);
                const formation = DataManager.getFormation(current.formationId);
                return {
                    occupied: true,
                    subject: subject ? subject.name : 'Clase',
                    prof: professor ? `${professor.firstName} ${professor.lastName}` : 'Profesor',
                    time: `${current.startTime} - ${current.endTime}`,
                    formation: formation ? formation.name : ''
                };
            }
            return { occupied: false };
        };

        // Load custom drawn zones from BrowserState dynamically
        const saved = BrowserState.getItem('ecpv_floorplan_zones');
        let rooms = [
            { id: 'zone_1', name: 'Aula 1', y: 15, x: 20, width: 12, height: 18 },
            { id: 'zone_2', name: 'Aula 2', y: 15, x: 33, width: 12, height: 18 },
            { id: 'zone_3', name: 'Aula 3', y: 15, x: 46, width: 12, height: 18 },
            { id: 'zone_4', name: 'Aula 4', y: 15, x: 59, width: 12, height: 18 },
            { id: 'zone_plato', name: 'Plató Principal', y: 38, x: 28, width: 28, height: 36 },
            { id: 'zone_sonido', name: 'Estudio de Sonido', y: 15, x: 72, width: 13, height: 18 },
            { id: 'zone_edicion', name: 'Sala de Edición', y: 48, x: 70, width: 15, height: 22 }
        ];
        if (saved) {
            try { rooms = JSON.parse(saved); } catch(e) {}
        }

        const roomsHtml = rooms.map(room => {
            const status = getClassroomStatus(room.name);
            const bgColor = status.occupied ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.1)';
            const border = status.occupied ? '2px solid #EF4444' : '2px dashed #10B981';
            const badgeBg = status.occupied ? '#EF4444' : '#10B981';
            
            return `
                <div class="map-room" data-room="${Utils.sanitizeHTML(room.name)}"
                     style="position: absolute; top: ${room.y}%; left: ${room.x}%; width: ${room.width}%; height: ${room.height}%; background: ${bgColor}; border: ${border}; border-radius: 8px; cursor: pointer; display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 0.5rem; text-align: center; transition: all 0.25s; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);"
                     ${UIEvents.attr("click", function(event,captured){ProgramView.showRoomDetails(String(captured[0]));}, [room.name])}
                     ${UIEvents.attr("mouseover", function(event,captured){this.style.transform='scale(1.02)';this.style.boxShadow='0 10px 15px -3px rgba(0,0,0,0.1)';}, [])}
                     ${UIEvents.attr("mouseout", function(event,captured){this.style.transform='scale(1)';this.style.boxShadow='0 4px 6px -1px rgba(0,0,0,0.05)';}, [])}>
                    <span style="font-weight: 800; font-size: 0.85rem; color: #1e293b;">${Utils.sanitizeHTML(room.name)}</span>
                    <span style="background: ${badgeBg}; color: white; font-size: 0.65rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; margin-top: 4px; text-transform: uppercase;">
                        ${status.occupied ? 'Ocupada' : 'Libre'}
                    </span>
                    ${status.occupied ? `<span style="font-size: 0.65rem; font-weight: 600; color: #ef4444; margin-top: 3px; max-width: 90%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${status.time}</span>` : ''}
                </div>
            `;
        }).join('');

        const content = `
            <div style="padding: 1rem; max-width: 1000px; margin: 0 auto;">
                <p style="color: var(--text-secondary); margin-bottom: 1.5rem;">
                    Mapa interactivo de las instalaciones del centro. Haz clic en cualquier aula para ver su ocupación y horarios de hoy.
                </p>

                <!-- Interactive Blueprint Container -->
                <div id="blueprint-container" style="position: relative; width: 100%; aspect-ratio: 1.41 / 1; border: 1.5px solid #cbd5e1; border-radius: 16px; background-image: url('/assets/plano_instalaciones.png'); background-size: cover; background-position: center; overflow: hidden; background-color: #f8fafc; box-shadow: inset 0 2px 4px rgba(0,0,0,0.03);">
                    <!-- Fallback SVG floorplan layout to show structure underneath rooms -->
                    <svg viewBox="0 0 1000 700" style="position: absolute; top:0; left:0; width:100%; height:100%; pointer-events:none; opacity: 0.25;">
                        <rect x="50" y="50" width="900" height="600" fill="none" stroke="#475569" stroke-width="3"/>
                        <rect x="250" y="250" width="300" height="280" fill="none" stroke="#475569" stroke-width="2"/>
                        <!-- Inner hallways -->
                        <line x1="50" y1="250" x2="950" y2="250" stroke="#475569" stroke-width="2"/>
                        <line x1="250" y1="250" x2="250" y2="650" stroke="#475569" stroke-width="2"/>
                        <line x1="550" y1="250" x2="550" y2="650" stroke="#475569" stroke-width="2"/>
                        <!-- Text overlay -->
                        <text x="400" y="220" font-family="sans-serif" font-size="14" font-weight="bold" fill="#64748b" text-anchor="middle">PASILLO CENTRAL</text>
                        <text x="400" y="560" font-family="sans-serif" font-size="14" font-weight="bold" fill="#64748b" text-anchor="middle">PATIO INTERIOR</text>
                    </svg>

                    <!-- Render Interactive Rooms -->
                    ${roomsHtml}
                </div>

                <div style="display: flex; justify-content: flex-end; margin-top: 1.5rem; border-top: 1px solid var(--border-color); padding-top: 1rem;">
                    <button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cerrar Mapa</button>
                </div>
            </div>
        `;

        Utils.openModal('Mapa de Aulas e Instalaciones', content);
    },

    showRoomDetails(roomName) {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const monthStr = String(month + 1).padStart(2, '0');
        const dayStr = String(day).padStart(2, '0');
        const dateString = `${year}-${monthStr}-${dayStr}`;

        const allClasses = DataManager.getAll(DataManager.KEYS.CLASSES) || [];
        const roomClasses = allClasses.filter(c => c.classroom === roomName && c.startDate === dateString)
            .sort((a, b) => a.startTime.localeCompare(b.startTime));

        let listHtml = '';
        if (roomClasses.length === 0) {
            listHtml = `
                <div style="text-align: center; padding: 2rem; background: #f0fdf4; border-radius: 12px; border: 1.5px solid #bbf7d0;">
                    <i class="fas fa-check-circle" style="color: #10b981; font-size: 2rem; margin-bottom: 0.5rem;"></i>
                    <h4 style="margin: 0; font-weight: 700; color: #065f46;">Aula Disponible</h4>
                    <p style="margin: 0.25rem 0 0 0; font-size: 0.85rem; color: #047857;">No hay sesiones programadas para hoy.</p>
                </div>
            `;
        } else {
            listHtml = `
                <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                    ${roomClasses.map(c => {
                        const subject = DataManager.getSubject(c.subjectId);
                        const professor = DataManager.getProfessor(c.professorId);
                        const formation = DataManager.getFormation(c.formationId);
                        return `
                            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid var(--primary-color); padding: 1rem; border-radius: 10px; display: flex; justify-content: space-between; align-items: center;">
                                <div>
                                    <span style="font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase;">${Utils.sanitizeHTML(formation ? formation.name : 'Curso')}</span>
                                    <h4 style="margin: 0.15rem 0; font-weight: 700; color: #1e293b; font-size: 1rem;">${Utils.sanitizeHTML(subject ? subject.name : 'Clase')}</h4>
                                    <span style="font-size: 0.8rem; color: #475569; display: flex; align-items: center; gap: 4px; margin-top: 2px;">
                                        <i class="fas fa-user-tie"></i> ${professor ? `${professor.firstName} ${professor.lastName}` : 'Profesor'}
                                    </span>
                                </div>
                                <div style="text-align: right;">
                                    <span style="background: #e0f2fe; color: #0369a1; font-size: 0.85rem; font-weight: 800; padding: 4px 10px; border-radius: 6px;">
                                        ${c.startTime} - ${c.endTime}
                                    </span>
                                </div>
                            </div>
                        `;
                    }).join('')}
                </div>
            `;
        }

        SafeHTML.fire({
            title: `Horarios de hoy - ${roomName}`,
            html: `
                <div style="text-align: left; margin-top: 1rem;">
                    ${listHtml}
                </div>
            `,
            confirmButtonColor: 'var(--primary-color)',
            confirmButtonText: 'Entendido'
        });
    }
};

// Make ProgramView globally available
window.ProgramView = ProgramView;
